import { useState } from 'react';
import { Check, X, Plus, Trash2, AlertTriangle } from 'lucide-react';
import apiService from '../../../services/apiService';

// The template for a domain-specific action: reads item.data (see
// backend/services/aimSchedulingAssembler.js), builds the real assign_contract_staffing
// confirmation via POST /api/aim/scheduling/assign (no name-extraction/NLU - AIM already
// knows exactly what's being requested), then approves/rejects it through the same
// universal "yes"/"no" chat mechanism every other confirmation in this app uses - no
// parallel approve/reject API.
//
// Defaults to a multi-person split (item.data.defaultSplit) rather than dumping an
// entire gap onto one person - each row capped at weeklyHoursCap under normal
// circumstances, only exceeding it (visibly flagged) when there genuinely isn't enough
// available capacity otherwise.
export default function SchedulingAssignAction({ item, onDone }) {
  const employees = item.data?.availableEmployees || [];
  const hoursNeeded = item.data?.hoursNeeded;
  const cap = item.data?.weeklyHoursCap || 40;
  const shortfallHours = item.data?.shortfallHours || 0;

  const [rows, setRows] = useState(
    (item.data?.defaultSplit || []).map(s => ({ employeeId: String(s.employeeId), hours: String(s.hours) }))
  );
  const [pending, setPending] = useState(null); // { confirmationId, message }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const usedIds = new Set(rows.map(r => r.employeeId).filter(Boolean));
  const totalHours = rows.reduce((sum, r) => sum + (parseFloat(r.hours) || 0), 0);
  const allRowsValid = rows.length > 0 && rows.every(r => r.employeeId && parseFloat(r.hours) > 0);
  const totalsMatch = hoursNeeded != null && Math.abs(totalHours - hoursNeeded) < 0.01;
  const canAssign = allRowsValid && totalsMatch;
  const canAddRow = employees.some(e => !usedIds.has(String(e.id)));

  const updateRow = (index, field, value) => {
    setRows(rows.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };
  const removeRow = (index) => setRows(rows.filter((_, i) => i !== index));
  const addRow = () => setRows([...rows, { employeeId: '', hours: '' }]);

  const propose = async () => {
    setBusy(true);
    setError(null);
    try {
      const assignments = rows.map(r => ({ employeeId: parseInt(r.employeeId, 10), hoursPerWeek: parseFloat(r.hours) }));
      const result = await apiService.assignScheduling(item.entityRef.id, assignments);
      setPending(result);
    } catch (err) {
      setError(err.message || 'Failed to propose assignment.');
    } finally {
      setBusy(false);
    }
  };

  const respond = async (approve) => {
    setBusy(true);
    setError(null);
    try {
      await apiService.sendChatMessage(approve ? 'yes' : 'no');
      setPending(null);
      if (approve) setRows([]);
      onDone?.();
    } catch (err) {
      setError(err.message || 'Failed to respond.');
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <div style={styles.pendingBox}>
        <div style={styles.pendingMessage}>{pending.message}</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button onClick={() => respond(true)} disabled={busy} style={styles.confirmBtn}>
            <Check size={14} /> Confirm
          </button>
          <button onClick={() => respond(false)} disabled={busy} style={styles.cancelBtn}>
            <X size={14} /> Cancel
          </button>
        </div>
        {error && <div style={styles.error}>{error}</div>}
      </div>
    );
  }

  if (employees.length === 0) return null;

  return (
    <div>
      {shortfallHours > 0 && (
        <div style={styles.shortfallBanner}>
          <AlertTriangle size={13} /> Not enough available capacity to cover this gap even with everyone at {cap} hrs/week - {shortfallHours} hrs would remain uncovered without exceeding the cap.
        </div>
      )}

      {rows.map((row, i) => {
        const rowHours = parseFloat(row.hours) || 0;
        const overCap = rowHours > cap;
        return (
          <div key={i} style={styles.pickerRow}>
            <select
              value={row.employeeId}
              onChange={e => updateRow(i, 'employeeId', e.target.value)}
              style={styles.select}
            >
              <option value="">Choose someone...</option>
              {employees
                .filter(e => String(e.id) === row.employeeId || !usedIds.has(String(e.id)))
                .map(e => (
                  <option key={e.id} value={e.id}>
                    {e.name}{e.role ? ` (${e.role})` : ''} - {e.currentHours}/{cap} hrs
                  </option>
                ))}
            </select>
            <input
              type="number"
              min="1"
              value={row.hours}
              onChange={e => updateRow(i, 'hours', e.target.value)}
              placeholder="hrs/wk"
              style={{ ...styles.hoursInput, ...(overCap ? styles.hoursInputOverCap : {}) }}
            />
            {overCap && <span style={styles.overCapBadge} title={`Exceeds the standard ${cap} hrs/week cap`}><AlertTriangle size={12} /> over cap</span>}
            <button onClick={() => removeRow(i)} style={styles.removeBtn} title="Remove">
              <Trash2 size={13} />
            </button>
          </div>
        );
      })}

      <div style={styles.footerRow}>
        {canAddRow && (
          <button onClick={addRow} style={styles.addRowBtn}>
            <Plus size={13} /> Add person
          </button>
        )}
        <span style={{ ...styles.totalLabel, color: totalsMatch ? '#22c55e' : '#94a3b8' }}>
          {totalHours} / {hoursNeeded ?? '?'} hrs/week
        </span>
        <button onClick={propose} disabled={busy || !canAssign} style={styles.assignBtn}>
          Assign
        </button>
      </div>
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}

const styles = {
  pickerRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  select: { padding: '6px 10px', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, color: '#e2e8f0', fontSize: 13, minWidth: 220 },
  hoursInput: { width: 70, padding: '6px 10px', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, color: '#e2e8f0', fontSize: 13 },
  hoursInputOverCap: { border: '1px solid rgba(245,158,11,0.6)' },
  overCapBadge: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#f59e0b', background: 'rgba(245,158,11,0.15)', padding: '3px 8px', borderRadius: 10 },
  removeBtn: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', padding: 6, display: 'flex', cursor: 'pointer' },
  footerRow: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 },
  addRowBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#3b82f6', fontSize: 12, cursor: 'pointer' },
  totalLabel: { fontSize: 13, fontWeight: 600, marginLeft: 'auto' },
  assignBtn: { padding: '6px 14px', background: 'linear-gradient(135deg,#6C63FF,#3b82f6)', border: 'none', borderRadius: 6, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  shortfallBanner: { display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, padding: '8px 12px', color: '#f59e0b', fontSize: 12, marginBottom: 10 },
  pendingBox: { background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, padding: 12, marginTop: 8, width: '100%' },
  pendingMessage: { color: '#94a3b8', fontSize: 12, whiteSpace: 'pre-wrap' },
  confirmBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#22c55e', fontSize: 12, cursor: 'pointer' },
  cancelBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', fontSize: 12, cursor: 'pointer' },
  error: { color: '#f87171', fontSize: 12, marginTop: 6 },
};
